import { useRouter } from 'expo-router'
import { Image } from 'expo-image'
import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { useIsFocused } from '@react-navigation/native'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import {
  FlatList,
  Platform,
  SafeAreaView,
  ScrollView,
  StatusBar as RnStatusBar,
  StyleSheet,
  TouchableOpacity,
  View
} from 'react-native'
import Animated, { FadeIn } from 'react-native-reanimated'

import Menu from '../../../assets/mainHeader/Menu.png'
import { useDrawerStore } from '@/components/Drawer/Drawer'
import { MainHeader } from '../../components/MainHeader/MainHeader'
import { DS } from '../../design-system'
import { events } from '../../models/analytics/analytics-events'
import { useStore } from '../../models/StoreProvider'
import { readArticleEventProps, selectVisibleArticles, useReadFeed } from './feed/readFeed'
import type { ReadArticle } from './feed/types'
import { ArticleListSkeleton } from './ArticleRowSkeleton'
import { useReadProgressStore } from './useReadProgressStore'

/** Sentinel for the unfiltered state, so the chip row is a plain string list. */
const ALL_CATEGORIES = 'All'

/**
 * Categories are derived from the feed rather than hardcoded: the CMS owns the
 * taxonomy, so a new category appears in the app the moment marketing uses it.
 */
const collectCategories = (articles: ReadArticle[]): string[] => {
  const seen = new Set<string>()
  articles.forEach((article) => article.categories?.forEach((category) => seen.add(category)))
  return [ALL_CATEGORIES, ...[...seen].sort((a, b) => a.localeCompare(b))]
}

const CategoryFilter = ({
  categories,
  selected,
  onSelect
}: {
  categories: string[]
  selected: string
  onSelect: (category: string) => void
}) => {
  const styles = categoryFilterStyles

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      // Without this the row is a flex child of the screen column and swells to
      // fill the space above the list, stretching every chip into a tall pill.
      style={styles.row}
      contentContainerStyle={styles.container}
      // Chips are small targets; keep the row from stealing vertical drags.
      directionalLockEnabled
    >
      {categories.map((category) => {
        const isSelected = category === selected
        return (
          <TouchableOpacity
            key={category}
            onPress={() => onSelect(category)}
            accessibilityRole="button"
            accessibilityState={{ selected: isSelected }}
            style={[styles.chip, isSelected && styles.chipSelected]}
            activeOpacity={0.8}
          >
            <DS.Text
              variant="Body Small/Medium"
              color={isSelected ? 'Global/White' : 'Brand/Dark Grey'}
              numberOfLines={1}
            >
              {category}
            </DS.Text>
          </TouchableOpacity>
        )
      })}
    </ScrollView>
  )
}

const formatPublishedAt = (iso: string) => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  // The CMS stores publish dates as UTC midnight; formatting in local time
  // showed US readers the day before (15 Sep → 14 Sep).
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

const ArticleRow = ({ article, onPress }: { article: ReadArticle; onPress: () => void }) => {
  const isCompleted = useReadProgressStore((state) => Boolean(state.byArticleId[article.id]?.completedAt))
  const progress = useReadProgressStore((state) => state.byArticleId[article.id]?.maxScroll ?? 0)
  const styles = articleRowStyles

  const meta = [
    formatPublishedAt(article.publishedAt),
    article.readTimeMinutes && `${article.readTimeMinutes} min read`
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <TouchableOpacity onPress={onPress} style={styles.container} activeOpacity={0.8}>
      {article.thumbnailUrl ? (
        <Image source={{ uri: article.thumbnailUrl }} style={styles.thumbnail} contentFit="cover" transition={150} />
      ) : (
        <View style={[styles.thumbnail, styles.thumbnailPlaceholder]}>
          <DS.Text variant="Heading 4/Regular" color="Brand/Dark Grey">
            {article.title.slice(0, 1).toUpperCase()}
          </DS.Text>
        </View>
      )}

      <View style={styles.content}>
        {article.categories?.length ? (
          <DS.Text variant="Mini/Medium" color="Brand/Maroon" uppercase>
            {article.categories[0]}
          </DS.Text>
        ) : null}

        <DS.Text variant="Body Medium/Medium" numberOfLines={3}>
          {article.title}
        </DS.Text>

        {article.excerpt ? (
          <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey" numberOfLines={2}>
            {article.excerpt}
          </DS.Text>
        ) : null}

        <View style={styles.metaRow}>
          <DS.Text variant="Mini/Regular" color="Brand/Dark Grey">
            {meta}
          </DS.Text>

          {isCompleted ? (
            <View style={styles.completedPill}>
              <DS.Icon name="Check" color="Utility/Success" size={12} />
              <DS.Text variant="Mini/Medium" color="Utility/Success">
                Read
              </DS.Text>
            </View>
          ) : progress > 0 ? (
            <DS.Text variant="Mini/Regular" color="Brand/Dark Grey">
              {`${Math.round(progress * 100)}%`}
            </DS.Text>
          ) : null}
        </View>
      </View>
    </TouchableOpacity>
  )
}

export const ReadScreen = observer(() => {
  const router = useRouter()
  const isFocused = useIsFocused()
  const { toggleDrawer } = useDrawerStore()
  const { data, isLoading } = useReadFeed()
  const {
    analytics: { setEvent }
  } = useStore()
  const styles = readScreenStyles

  const [selectedCategory, setSelectedCategory] = useState<string>(ALL_CATEGORIES)

  const articles = useMemo(() => (data ? selectVisibleArticles(data) : []), [data])
  const categories = useMemo(() => collectCategories(articles), [articles])

  const visibleArticles = useMemo(
    () =>
      selectedCategory === ALL_CATEGORIES
        ? articles
        : articles.filter((article) => article.categories?.includes(selectedCategory)),
    [articles, selectedCategory]
  )

  // A category can disappear when the feed refreshes; fall back to All rather
  // than stranding the reader on an empty list.
  useEffect(() => {
    if (!categories.includes(selectedCategory)) {
      setSelectedCategory(ALL_CATEGORIES)
    }
  }, [categories, selectedCategory])

  const selectCategory = (category: string) => {
    if (category === selectedCategory) {
      return
    }
    setEvent(events.read.filter_tapped, {
      category,
      previous_category: selectedCategory,
      article_count:
        category === ALL_CATEGORIES
          ? articles.length
          : articles.filter((article) => article.categories?.includes(category)).length
    })
    setSelectedCategory(category)
  }

  const markOpened = useReadProgressStore((state) => state.markOpened)

  const openArticle = (article: ReadArticle, index: number) => {
    if (Platform.OS === 'web') {
      // The in-app reader is a WebView, which cannot inject into a cross-origin
      // page on web. Read it on the corp site in a new tab instead, keeping the
      // app open behind it. Scroll progress is not reported from there.
      const { maxScroll = 0, completedAt } = useReadProgressStore.getState().byArticleId[article.id] ?? {}
      setEvent(events.read.article_opened, {
        ...readArticleEventProps(article),
        article_index: index,
        filter_category: selectedCategory,
        source: 'read_list',
        previous_percent_read: Math.round(maxScroll * 100),
        previously_completed: Boolean(completedAt)
      })
      markOpened(article.id)
      window.open(article.url, '_blank', 'noopener,noreferrer')
      return
    }
    router.navigate({
      pathname: '/read/[articleId]',
      // The reader fires the article analytics events (so deep-link opens count
      // too) and reports where in the list the article was tapped.
      params: { articleId: article.id, articleIndex: String(index), filterCategory: selectedCategory }
    })
  }

  return (
    <Animated.View style={{ flex: 1 }} entering={FadeIn}>
      <SafeAreaView style={styles.container}>
        {isFocused ? <StatusBar style={'dark' as StatusBarStyle} /> : null}
        <MainHeader
          hideLeftDefaultIcon
          title="Read"
          titleStyle={DS.typography['Heading 2/Regular']}
          rightIcon={Menu}
          rightPress={() => toggleDrawer()}
        />

        {categories.length > 2 ? (
          <CategoryFilter categories={categories} selected={selectedCategory} onSelect={selectCategory} />
        ) : null}

        <FlatList
          data={visibleArticles}
          keyExtractor={(article) => article.id}
          renderItem={({ item, index }) => <ArticleRow article={item} onPress={() => openArticle(item, index)} />}
          contentContainerStyle={[styles.list, containerStyles.webWrapper]}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={
            isLoading ? (
              <ArticleListSkeleton />
            ) : (
              <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey">
                {selectedCategory === ALL_CATEGORIES ? 'No articles yet.' : `Nothing in ${selectedCategory} yet.`}
              </DS.Text>
            )
          }
        />
      </SafeAreaView>
    </Animated.View>
  )
})

const readScreenStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream'],
    paddingTop: Platform.OS === 'android' ? RnStatusBar.currentHeight : 0
  },
  list: {
    padding: DS.spacing[24],
    gap: DS.spacing[16]
  }
})

const articleRowStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: DS.spacing[16],
    backgroundColor: DS.palette['Global/White'],
    borderRadius: DS.spacing[8],
    overflow: 'hidden',
    padding: DS.spacing[12]
  },
  thumbnail: {
    width: 96,
    height: 96,
    borderRadius: DS.spacing[6],
    backgroundColor: DS.palette['Brand/Light Grey']
  },
  thumbnailPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    flex: 1,
    gap: DS.spacing[4],
    justifyContent: 'center'
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: DS.spacing[8],
    marginTop: DS.spacing[4]
  },
  completedPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DS.spacing[4]
  }
})

const categoryFilterStyles = StyleSheet.create({
  row: {
    flexGrow: 0,
    flexShrink: 0
  },
  container: {
    gap: DS.spacing[8],
    paddingHorizontal: DS.spacing[24],
    paddingVertical: DS.spacing[4],
    // Chips size to their own text instead of stretching to the row height.
    alignItems: 'center'
  },
  chip: {
    paddingHorizontal: DS.spacing[16],
    paddingVertical: DS.spacing[8],
    borderRadius: 40,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    backgroundColor: DS.palette['Global/White']
  },
  chipSelected: {
    backgroundColor: DS.palette['Brand/Maroon'],
    borderColor: DS.palette['Brand/Maroon']
  }
})

const containerStyles = StyleSheet.create({
  webWrapper: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center'
  }
})
